import { trackingAction } from './itemsTrackingActions';
import { QuestionWidgetTrackingActions } from './questionWidgetTrackingActions';
import { ContentItemType, SingleResponseQuestionWidget, TrackingIdentifier } from '@ael/core-api';
import { AnyAction } from '@reduxjs/toolkit';
import { Map } from 'immutable';

export type PreviouslySelectedOptionsForItems = Map<TrackingIdentifier, PreviouslySelectedOptionsForWidgets>;
export type PreviouslySelectedOptionsForWidgets = Map<TrackingIdentifier, number>;

export type QuestionWidgetTrackingState = {
  previouslySelectedOptionsForItems: PreviouslySelectedOptionsForItems;
};

export const emptyQuestionWidgetTrackingState: QuestionWidgetTrackingState = {
  previouslySelectedOptionsForItems: Map<TrackingIdentifier, PreviouslySelectedOptionsForWidgets>(),
};

export function createQuestionWidgetTrackingReducer(startState: QuestionWidgetTrackingState) {
  return (state: QuestionWidgetTrackingState = startState, action: AnyAction): QuestionWidgetTrackingState => {
    switch (action.type) {
      case QuestionWidgetTrackingActions.SetSelectedOption: {
        const contentItem = action.payload.contentItem as ContentItemType;
        const questionWidget = action.payload.questionWidget as SingleResponseQuestionWidget;
        const selectedOption = action.payload.selectedOption as number;

        return {
          previouslySelectedOptionsForItems: updatePreviouslySelectedOptionsForItem(
            state.previouslySelectedOptionsForItems,
            { widgetId: questionWidget.trackingIdentifier, selectedOptionIndex: selectedOption },
            contentItem.trackingIdentifier
          ),
        };
      }
      case QuestionWidgetTrackingActions.ResetSelectedOption: {
        const contentItem = action.payload.contentItem as ContentItemType;
        const questionWidget = action.payload.questionWidget as SingleResponseQuestionWidget;

        return {
          previouslySelectedOptionsForItems: removePreviouslySelectedOptionsForItem(
            state.previouslySelectedOptionsForItems,
            questionWidget.trackingIdentifier,
            contentItem.trackingIdentifier
          ),
        };
      }

      case trackingAction.Tracking_ResetProgress:
        return emptyQuestionWidgetTrackingState;

      default:
        return state;
    }
  };
}

type ItemPreviouslySelectedOptionsPair = {
  itemId: TrackingIdentifier;
  previouslySelectedOptionsForWidgets: Map<TrackingIdentifier, number>;
};

export const createPreviouslySelectedOptionsForItems = (
  pairs: ItemPreviouslySelectedOptionsPair[]
): PreviouslySelectedOptionsForItems => {
  return Map<TrackingIdentifier, PreviouslySelectedOptionsForWidgets>(
    pairs.map((pair) => [pair.itemId, pair.previouslySelectedOptionsForWidgets])
  );
};
type WidgetSelectedOptionPair = {
  widgetId: TrackingIdentifier;
  selectedOptionIndex: number;
};

export const createPreviouslySelectedOptionsForWidgets = (
  pairs: WidgetSelectedOptionPair[]
): PreviouslySelectedOptionsForWidgets => {
  return Map<TrackingIdentifier, number>(pairs.map((pair) => [pair.widgetId, pair.selectedOptionIndex]));
};
const removePreviouslySelectedOptionsForWidget = (
  current: PreviouslySelectedOptionsForWidgets,
  widgetTrackingId: TrackingIdentifier
): PreviouslySelectedOptionsForWidgets => {
  return current.remove(widgetTrackingId);
};
const updatePreviouslySelectedOptionsForWidget = (
  current: PreviouslySelectedOptionsForWidgets,
  update: WidgetSelectedOptionPair
): PreviouslySelectedOptionsForWidgets => {
  return current.set(update.widgetId, update.selectedOptionIndex);
};

const updatePreviouslySelectedOptionsForItem = (
  current: PreviouslySelectedOptionsForItems,
  update: WidgetSelectedOptionPair,
  contentItemTrackingIdentifier: TrackingIdentifier
): PreviouslySelectedOptionsForItems => {
  const existing = current.get(contentItemTrackingIdentifier);
  if (!existing) {
    return current.set(
      contentItemTrackingIdentifier,
      createPreviouslySelectedOptionsForWidgets([
        { widgetId: update.widgetId, selectedOptionIndex: update.selectedOptionIndex },
      ])
    );
  }
  const updated = updatePreviouslySelectedOptionsForWidget(existing, {
    widgetId: update.widgetId,
    selectedOptionIndex: update.selectedOptionIndex,
  });
  return current.set(contentItemTrackingIdentifier, updated);
};

const removePreviouslySelectedOptionsForItem = (
  current: PreviouslySelectedOptionsForItems,
  widgetId: TrackingIdentifier,
  contentItemTrackingIdentifier: TrackingIdentifier
): PreviouslySelectedOptionsForItems => {
  const existing = current.get(contentItemTrackingIdentifier);
  if (!existing) {
    return current.remove(contentItemTrackingIdentifier);
  }
  const updated = removePreviouslySelectedOptionsForWidget(existing, widgetId);
  return current.set(contentItemTrackingIdentifier, updated);
};
